(() => {
  const ready = (callback) => {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', callback, { once: true });
    else callback();
  };

  const initHero = (root) => {
    const slides = Array.from(root.querySelectorAll('[data-hero-slide]'));
    const dots = Array.from(root.querySelectorAll('[data-hero-dot]'));
    const previous = root.querySelector('[data-hero-prev]');
    const next = root.querySelector('[data-hero-next]');
    const pause = root.querySelector('[data-hero-pause]');
    if (slides.length < 2) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const delay = Number(root.dataset.autoplay) || 7000;
    let index = 0;
    let timer = null;
    let pausedByUser = reducedMotion;
    let touchStartX = 0;

    const stop = () => {
      window.clearTimeout(timer);
      timer = null;
    };

    const schedule = () => {
      stop();
      if (!pausedByUser && !document.hidden) timer = window.setTimeout(() => show(index + 1), delay);
    };

    const show = (requestedIndex, userInitiated = false) => {
      index = (requestedIndex + slides.length) % slides.length;
      slides.forEach((slide, slideIndex) => {
        const active = slideIndex === index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        if ('inert' in slide) slide.inert = !active;
        slide.querySelectorAll('a, button, input, select, textarea').forEach((element) => {
          if (active) element.removeAttribute('tabindex');
          else element.setAttribute('tabindex', '-1');
        });
      });
      dots.forEach((dot, dotIndex) => {
        const active = dotIndex === index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', String(active));
      });
      if (userInitiated) stop();
      schedule();
    };

    previous?.addEventListener('click', () => show(index - 1, true));
    next?.addEventListener('click', () => show(index + 1, true));
    dots.forEach((dot) => dot.addEventListener('click', () => show(Number(dot.dataset.heroDot), true)));

    pause?.addEventListener('click', () => {
      pausedByUser = !pausedByUser;
      pause.setAttribute('aria-pressed', String(pausedByUser));
      pause.setAttribute('aria-label', pausedByUser ? 'Play automatic slides' : 'Pause automatic slides');
      const icon = pause.querySelector('span');
      if (icon) icon.textContent = pausedByUser ? '▶' : 'Ⅱ';
      if (pausedByUser) stop();
      else schedule();
    });
    if (reducedMotion && pause) {
      pause.setAttribute('aria-pressed', 'true');
      pause.setAttribute('aria-label', 'Play automatic slides');
      const icon = pause.querySelector('span');
      if (icon) icon.textContent = '▶';
    }

    root.addEventListener('mouseenter', stop);
    root.addEventListener('mouseleave', schedule);
    root.addEventListener('focusin', stop);
    root.addEventListener('focusout', () => window.setTimeout(() => {
      if (!root.contains(document.activeElement)) schedule();
    }, 0));
    root.addEventListener('touchstart', (event) => {
      touchStartX = event.changedTouches[0]?.clientX || 0;
      stop();
    }, { passive: true });
    root.addEventListener('touchend', (event) => {
      const distance = (event.changedTouches[0]?.clientX || 0) - touchStartX;
      if (Math.abs(distance) > 45) show(index + (distance < 0 ? 1 : -1), true);
      else schedule();
    }, { passive: true });
    document.addEventListener('visibilitychange', () => document.hidden ? stop() : schedule());

    show(0);
  };

  const initMobileMenus = () => {
    const menus = Array.from(document.querySelectorAll('[data-mobile-menu]'));
    menus.forEach((menu) => {
      const summary = menu.querySelector('summary');
      const closeButton = menu.querySelector('[data-mobile-menu-close]');
      const scrim = menu.querySelector('[data-mobile-menu-scrim]');

      const close = (restoreFocus = true) => {
        menu.open = false;
        summary?.setAttribute('aria-expanded', 'false');
        document.body.classList.remove('menu-open');
        if (restoreFocus) summary?.focus();
      };

      menu.addEventListener('toggle', () => {
        summary?.setAttribute('aria-expanded', String(menu.open));
        summary?.setAttribute('aria-label', menu.open ? 'Close menu' : 'Open menu');
        document.body.classList.toggle('menu-open', menu.open);
        if (menu.open) {
          menus.filter((candidate) => candidate !== menu).forEach((candidate) => { candidate.open = false; });
          window.setTimeout(() => closeButton?.focus(), 0);
        }
      });
      closeButton?.addEventListener('click', () => close());
      scrim?.addEventListener('click', () => close());
      menu.querySelectorAll('.mobile-drawer a').forEach((link) => link.addEventListener('click', () => close(false)));
      menu.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          close();
        } else if (event.key === 'Tab' && menu.open) {
          const focusable = Array.from(menu.querySelectorAll('.mobile-drawer button:not([disabled]), .mobile-drawer a[href]'));
          if (!focusable.length) return;
          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        }
      });
    });

    window.addEventListener('resize', () => {
      if (window.innerWidth > 760) menus.forEach((menu) => { menu.open = false; });
    });
  };

  const initCondensedHeader = () => {
    const header = document.querySelector('[data-site-header]');
    if (!header) return;
    let ticking = false;
    const update = () => {
      header.classList.toggle('is-condensed', window.innerWidth <= 760 && window.scrollY > 110);
      ticking = false;
    };
    const requestUpdate = () => {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(update);
      }
    };
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);
    update();
  };

  const initStickyPurchase = (bar) => {
    const root = document.querySelector('[data-product-root]');
    const stickyButton = bar.querySelector('[data-sticky-purchase]');
    const form = root?.querySelector('.product-form');
    const originalButton = root?.querySelector('[data-add-button]');
    if (!root || !stickyButton || !form || !originalButton) return;

    const isKg = root.hasAttribute('data-kg-product');
    const mode = stickyButton.dataset.stickyMode;
    const reveal = (visible) => bar.classList.toggle('is-visible', visible && window.innerWidth <= 760);

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(([entry]) => reveal(!entry.isIntersecting && window.scrollY > 140), { threshold: 0.05 });
      observer.observe(originalButton);
    } else {
      window.addEventListener('scroll', () => reveal(window.scrollY > 520), { passive: true });
    }

    const focusForm = () => {
      form.scrollIntoView({ behavior: 'smooth', block: 'center' });
      window.setTimeout(() => {
        const target = form.querySelector('[data-kg-input]:not([disabled]), [data-variant-select], [data-add-button]:not([disabled])');
        target?.focus({ preventScroll: true });
      }, 450);
    };

    stickyButton.addEventListener('click', () => {
      if (stickyButton.disabled) return;
      if (mode === 'submit' && !originalButton.disabled) originalButton.click();
      else focusForm();
    });

    if (!isKg) {
      const sync = () => {
        stickyButton.disabled = originalButton.disabled;
        stickyButton.setAttribute('aria-disabled', String(originalButton.disabled));
        stickyButton.textContent = originalButton.textContent.trim();
      };
      new MutationObserver(sync).observe(originalButton, { attributes: true, childList: true, characterData: true, subtree: true });
      root.querySelector('[data-variant-select]')?.addEventListener('change', () => window.setTimeout(sync, 0));
      sync();
    }
  };

  ready(() => {
    document.querySelectorAll('[data-hero-slider]').forEach(initHero);
    initMobileMenus();
    initCondensedHeader();
    document.querySelectorAll('[data-mobile-purchase]').forEach(initStickyPurchase);
  });
})();
